import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, Plus, Trash2, ScrollText, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImportDialog from "@/components/ImportDialog";

const ACTION_STYLES = {
    create: "bg-emerald-50 text-emerald-700 border-emerald-200",
    update: "bg-blue-50 text-blue-700 border-blue-200",
    delete: "bg-red-50 text-red-700 border-red-200",
    checkin: "bg-amber-50 text-amber-700 border-amber-200",
    status: "bg-slate-100 text-slate-600 border-slate-200",
    admit: "bg-violet-50 text-violet-700 border-violet-200",
    discharge: "bg-teal-50 text-teal-700 border-teal-200",
    add: "bg-emerald-50 text-emerald-700 border-emerald-200",
    remove: "bg-red-50 text-red-700 border-red-200",
    promote: "bg-blue-50 text-blue-700 border-blue-200",
};

const EMPTY_ENTRY = { entry_type: "MTP", date: new Date().toISOString().slice(0, 10), patient_name: "", doctor_name: "", indication: "", gestation_weeks: "", method: "", form_no: "", consent_obtained: false, remarks: "" };

export default function Compliance() {
    const [tab, setTab] = useState("register");
    const [regType, setRegType] = useState("MTP");
    const [entries, setEntries] = useState(null);
    const [logs, setLogs] = useState(null);
    const [entityFilter, setEntityFilter] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(EMPTY_ENTRY);
    const [saving, setSaving] = useState(false);

    const loadRegister = async () => {
        try {
            const { data } = await api.get("/compliance", { params: { entry_type: regType } });
            setEntries(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const loadAudit = async () => {
        try {
            const { data } = await api.get("/audit", { params: { entity: entityFilter, limit: 200 } });
            setLogs(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => { setEntries(null); loadRegister(); }, [regType]);
    useEffect(() => { if (tab === "audit") { setLogs(null); loadAudit(); } }, [tab, entityFilter]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/compliance", { ...form, entry_type: regType });
            toast.success(`${regType} entry recorded`);
            setShowAdd(false);
            setForm(EMPTY_ENTRY);
            loadRegister();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const remove = async (entry) => {
        if (!window.confirm(`Delete ${entry.entry_type} entry for ${entry.patient_name} (${entry.date})?`)) return;
        try {
            await api.delete(`/compliance/${entry.id}`);
            toast.success("Entry deleted");
            loadRegister();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    return (
        <div className="space-y-5 animate-fade-up" data-testid="compliance-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Compliance</h1>
                    <p className="mt-1 text-base text-slate-500">MTP / PCPNDT statutory register and clinic audit trail</p>
                </div>
                <div className="flex gap-2">
                    {tab === "register" && <ImportDialog module="compliance" label="Import" onDone={loadRegister} />}
                    {tab === "register" && (
                        <Button data-testid="add-compliance-btn" onClick={() => { setForm({ ...EMPTY_ENTRY, entry_type: regType }); setShowAdd(true); }} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-2"><Plus className="h-4 w-4" /> New {regType} Entry</Button>
                    )}
                </div>
            </div>

            <div className="flex gap-1 border-b border-slate-200" data-testid="compliance-tabs">
                {[["register", "MTP / PCPNDT Register"], ["audit", "Audit Trail"]].map(([k, l]) => (
                    <button key={k} data-testid={`compliance-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 ${tab === k ? "border-[#E06D1F] text-[#C2410C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
                ))}
            </div>

            {tab === "register" && (
                <div className="space-y-4">
                    <div className="flex gap-2" data-testid="register-type-toggle">
                        {["MTP", "PCPNDT"].map((t) => (
                            <button key={t} data-testid={`register-type-${t}`} onClick={() => setRegType(t)} className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors duration-150 ${regType === t ? "bg-[#E06D1F] border-[#E06D1F] text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{t === "MTP" ? "MTP Register" : "PCPNDT (Form F) Register"}</button>
                        ))}
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="compliance-register-card">
                        {!entries ? (
                            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                        ) : entries.length === 0 ? (
                            <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="compliance-empty">No {regType} entries yet.</p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                    <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Patient</th><th className="px-4 py-2.5 hidden md:table-cell">Indication</th><th className="px-4 py-2.5 hidden sm:table-cell">{regType === "MTP" ? "GA / Method" : "Study"}</th><th className="px-4 py-2.5 hidden lg:table-cell">Form No</th><th className="px-4 py-2.5">Consent</th><th className="px-4 py-2.5"></th>
                                </tr></thead>
                                <tbody>
                                    {entries.map((en) => (
                                        <tr key={en.id} data-testid={`compliance-row-${en.id}`} className="border-t border-slate-100">
                                            <td className="px-4 py-2.5 text-slate-700">{en.date}</td>
                                            <td className="px-4 py-2.5 font-medium text-slate-900">{en.patient_name}<p className="text-[11px] text-slate-400 font-normal">{en.doctor_name}</p></td>
                                            <td className="px-4 py-2.5 hidden md:table-cell text-slate-600 max-w-[220px] truncate">{en.indication || "—"}</td>
                                            <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">{[en.gestation_weeks && `${en.gestation_weeks} wk`, en.method].filter(Boolean).join(" · ") || "—"}</td>
                                            <td className="px-4 py-2.5 hidden lg:table-cell font-mono2 text-xs text-slate-600">{en.form_no || "—"}</td>
                                            <td className="px-4 py-2.5">{en.consent_obtained ? <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`consent-badge-${en.id}`}>Taken</span> : <span className="rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-600">Pending</span>}</td>
                                            <td className="px-4 py-2.5 text-right"><Button size="sm" variant="ghost" data-testid={`compliance-delete-${en.id}`} onClick={() => remove(en)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {tab === "audit" && (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="audit-card">
                    <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ScrollText className="h-4 w-4 text-[#E06D1F]" /> Audit Trail</h3>
                        <select data-testid="audit-entity-filter" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-orange-500 outline-none">
                            <option value="">All activity</option>
                            {["patient", "visit", "appointment", "queue", "waitlist", "ipd", "team", "screening", "contraception", "cycle", "fertility", "compliance"].map((en) => <option key={en} value={en}>{en}</option>)}
                        </select>
                    </div>
                    {!logs ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                    ) : logs.length === 0 ? (
                        <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="audit-empty">No activity recorded yet. Actions like adding patients, visits and tokens appear here.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-4 py-2.5">Time</th><th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Action</th><th className="px-4 py-2.5">Module</th><th className="px-4 py-2.5 hidden md:table-cell">Details</th>
                            </tr></thead>
                            <tbody>
                                {logs.map((l) => (
                                    <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-t border-slate-100">
                                        <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{new Date(l.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                                        <td className="px-4 py-2.5 text-slate-700">{l.user_name}</td>
                                        <td className="px-4 py-2.5"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${ACTION_STYLES[l.action] || "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`audit-action-${l.id}`}>{l.action}</span></td>
                                        <td className="px-4 py-2.5 capitalize text-slate-600">{l.entity}</td>
                                        <td className="px-4 py-2.5 hidden md:table-cell text-xs text-slate-500 max-w-[280px] truncate">{l.detail || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="compliance-add-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2"><FileCheck2 className="h-5 w-5 text-[#E06D1F]" /> New {regType} Entry</DialogTitle>
                        <DialogDescription>{regType === "MTP" ? "Medical Termination of Pregnancy register (MTP Act)." : "PCPNDT Form F register for ultrasound studies on pregnant women."}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Date *</Label><Input data-testid="comp-date-input" type="date" required value={form.date} onChange={set("date")} /></div>
                            <div className="space-y-1.5"><Label>{regType === "PCPNDT" ? "Form F No" : "Form C No"}</Label><Input data-testid="comp-formno-input" value={form.form_no} onChange={set("form_no")} /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Patient Name *</Label><Input data-testid="comp-patient-input" required value={form.patient_name} onChange={set("patient_name")} /></div>
                        <div className="space-y-1.5"><Label>Doctor</Label><Input data-testid="comp-doctor-input" value={form.doctor_name} onChange={set("doctor_name")} placeholder="Performing / referring doctor" /></div>
                        <div className="space-y-1.5"><Label>Indication</Label><Input data-testid="comp-indication-input" value={form.indication} onChange={set("indication")} placeholder={regType === "MTP" ? "e.g. contraceptive failure, medical indication" : "e.g. ANC growth scan, anomaly scan"} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Gestation (weeks)</Label><Input data-testid="comp-weeks-input" type="number" min="0" max="42" value={form.gestation_weeks} onChange={set("gestation_weeks")} /></div>
                            <div className="space-y-1.5"><Label>{regType === "MTP" ? "Method" : "Study"}</Label><Input data-testid="comp-method-input" value={form.method} onChange={set("method")} placeholder={regType === "MTP" ? "Medical / Surgical (MVA / D&E)" : "USG pelvis / obstetric"} /></div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="comp-consent-label">
                            <input type="checkbox" data-testid="comp-consent-checkbox" checked={form.consent_obtained} onChange={set("consent_obtained")} className="h-4 w-4 rounded border-slate-300 text-[#E06D1F] focus:ring-orange-500" />
                            Written informed consent obtained
                        </label>
                        <div className="space-y-1.5"><Label>Remarks</Label><Textarea data-testid="comp-remarks-input" rows={2} value={form.remarks} onChange={set("remarks")} /></div>
                        <Button data-testid="comp-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Entry</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
